// assets/AllCourses/Exercises/DragAndDropExercise.jsx

import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/utils";
import useExerciseEngine from "@/hooks/useExerciseEngine";

export default function DragAndDropExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  showResults = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.dragAndDropQuestions)
    ? exercise.dragAndDropQuestions
    : [];

  /* ================= ENGINE ================= */
 const {
  answers,
  setAnswer,
  submitIfReady,
  getInputState,
} = useExerciseEngine({
  questions: questions.map((q) => ({
    ...q,
    type: "fill",
  })),
  showResult: showResults,
  onComplete,
 externalAnswers: userAnswer,
});

  /* ================= LOCAL STATE (UI ONLY) ================= */
  const [uiState, setUiState] = useState([]);

  useEffect(() => {
  if (!questions.length) return;

  const initial = questions.map((q, qIdx) => {
    const totalBlanks = (q.sentence.match(/___/g) || []).length;

    const droppedAnswers = Array(totalBlanks).fill("");

    // ✅ restore from engine answers
    for (let i = 0; i < totalBlanks; i++) {
      const key = `${qIdx}-${i}`;
      if (answers[key]) {
        droppedAnswers[i] = answers[key];
      }
    }

    // remove used options
    const used = droppedAnswers.filter(Boolean);

    const availableOptions = (q.options || []).filter(
      (opt) => !used.includes(opt)
    );

    return {
      availableOptions,
      droppedAnswers,
    };
  });

  setUiState(initial);
}, [questions, answers]);

  /* ================= DROP ================= */
  const handleDrop = (qIdx, blankIdx, option) => {
    if (showResults || !option) return;

    setUiState((prev) => {
      const next = structuredClone(prev);
      const qState = next[qIdx];

      if (!qState.availableOptions.includes(option)) return prev;

      qState.droppedAnswers[blankIdx] = option;
      qState.availableOptions = qState.availableOptions.filter(
        (o) => o !== option
      );

      const key = `${qIdx}-${blankIdx}`;
      setAnswer(key, option);

      submitIfReady();

      return next;
    });
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return <div>Edit Mode (keep your existing UI)</div>;
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No questions available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT UI ================= */
  return (
    <Card className="max-w-4xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Drag and Drop</CardTitle>
        <CardDescription>
          Drag the words into the correct blanks
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const parts = q.sentence.split("___");
          const qState = uiState[qIdx] || {
            availableOptions: [],
            droppedAnswers: [],
          };

          return (
            <div key={qIdx} className="space-y-6">
              {/* WORD BANK */}
              <div className="rounded-xl border bg-muted/40 p-4">
                <div className="flex flex-wrap justify-center gap-3 min-h-[64px]">
                  {qState.availableOptions.length ? (
                    qState.availableOptions.map((opt) => (
                      <Button
                        key={opt}
                        draggable={!showResults}
                        size="sm"
                        variant="secondary"
                        onDragStart={(e) =>
                          e.dataTransfer.setData("text/plain", opt)
                        }
                      >
                        {opt}
                      </Button>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      All words used
                    </p>
                  )}
                </div>
              </div>

              {/* SENTENCE */}
              <div className="rounded-xl border p-6 flex flex-wrap gap-3 text-lg">
                {parts.map((part, i) => {
                  const key = `${qIdx}-${i}`;
                  const value = answers[key] || "";
                  const correct = q.answer?.[i] || "";

                  const state = getInputState(value, correct);

                  return (
                    <React.Fragment key={i}>
                      {part}

                      {i < parts.length - 1 && (
                        <div
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) =>
                            handleDrop(
                              qIdx,
                              i,
                              e.dataTransfer.getData("text/plain")
                            )
                          }
                          className={cn(
                            "min-w-[120px] h-10 border-2 border-dashed flex items-center justify-center transition",
                            state.className,
                            showResults && "cursor-not-allowed"
                          )}
                        >
                          {value || "___"}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}